import {
	compact,
	isWeb,
	noop,
	openCookieConsent,
	type PreParsedSectionItemsSettingProps,
	type PreParsedSectionSettingProps,
	useAppTheme,
	useSettings,
	useTutorialsCompleted
} from '@capsicum/common';
import { useEffect, useMemo, useState } from 'react';

import { useFilterOtherSection } from './useFilterOtherSection';

// TODO:  this dummy function can be removed once it's not called anymore as a mock on this file
const dummyFunction = noop;

const recoverWallet: PreParsedSectionItemsSettingProps = {
	action: 'applyRecoveryKit',
	isTrailingIconReversible: true,
	onPress: noop,
	testID: 'settings-recoverWallet-listItem',
	title: 'onboard.settings.recoverWallet',
	trailingIcon: 'ChevronRightIcon',
	webView: false
};

const generateRecoveryKit: PreParsedSectionItemsSettingProps = {
	action: 'generateRecoveryKit',
	isTrailingIconReversible: true,
	onPress: noop,
	testID: 'settings-generateRecoveryKit-listItem',
	title: 'onboard.settings.recoveryKit',
	trailingIcon: 'ChevronRightIcon',
	webView: false
};

const withdrawalPassword: PreParsedSectionItemsSettingProps = {
	action: 'fetchCurrentWithdrawalPassword',
	isTrailingIconReversible: true,
	onPress: noop,
	testID: 'settings-withdrawalPassword-listItem',
	title: 'onboard.settings.withdrawalPassword',
	trailingIcon: 'ChevronRightIcon',
	webView: false
};

const useSecuritySection = () => {
	const {
		settings: { isPasswordSettingEnabled, isPoblanoEnabled }
	} = useSettings();

	return useMemo(() => {
		const securityItems: Array<PreParsedSectionItemsSettingProps> = isPoblanoEnabled
			? [recoverWallet, generateRecoveryKit]
			: [withdrawalPassword];

		const section: PreParsedSectionSettingProps = {
			items: securityItems,
			title: 'onboard.settings.security'
		};

		return section;
	}, [isPasswordSettingEnabled, isPoblanoEnabled]);
};

const generalItems: Array<PreParsedSectionItemsSettingProps> = [
	{
		action: 'onboard.settings.configure',
		isTrailingIconReversible: true,
		onPress: dummyFunction,
		testID: 'settings-comms-notifications',
		title: 'onboard.settings.notifications',
		trailingIcon: 'ChevronRightIcon',
		webView: true
	},
	{
		action: 'fetchLanguage',
		isTrailingIconReversible: true,
		onPress: dummyFunction,
		testID: 'settings-language-listItem',
		title: 'onboard.settings.language',
		trailingIcon: 'ChevronRightIcon',
		webView: true
	},
	{
		action: 'fetchCurrency',
		isTrailingIconReversible: true,
		onPress: dummyFunction,
		testID: 'settings-currency-listItem',
		title: 'onboard.settings.currency',
		trailingIcon: 'ChevronRightIcon',
		webView: true
	},
	{
		action: 'fetchFavoritePartner',
		isTrailingIconReversible: true,
		onPress: dummyFunction,
		testID: 'settings-favTeam-listItem',
		title: 'onboard.settings.favouriteTeam',
		trailingIcon: 'ChevronRightIcon',
		webView: true
	},
	{
		action: 'onboard.settings.enableTutorial',
		borderRadius: 8,
		onPress: dummyFunction,
		testID: 'settings-beginnerMode-listItem',
		title: 'onboard.settings.beginnerMode',
		webView: true
	},
	{
		action: 'setTheme',
		isTrailingIconReversible: true,
		onPress: dummyFunction,
		testID: 'settings-appearance',
		title: 'onboard.settings.appearance.title',
		trailingIcon: 'ChevronRightIcon',
		webView: false
	}
];

if (isWeb()) {
	generalItems.push({
		isTrailingIconReversible: true,
		onPress: openCookieConsent,
		testID: 'settings-accountManagement-cookiePreferences',
		title: 'cookieConsent.preferencesAccess',
		trailingIcon: 'ChevronRightIcon',
		webView: false
	});
}

const accountSection: PreParsedSectionSettingProps = {
	items: [
		{
			action: 'onboard.settings.manage',
			isTrailingIconReversible: true,
			onClick: dummyFunction,
			testID: 'settings-accountManagement-listItem',
			title: 'settings.accountManagement.account',
			trailingIcon: 'ChevronRightIcon',
			webView: true
		},
		{
			action: 'settings.accountManagement.marketingPreferences.action',
			isTrailingIconReversible: true,
			testID: 'settings-accountManagement-marketingPreference-listItem',
			title: 'settings.accountManagement.marketingPreferences.title',
			trailingIcon: 'ChevronRightIcon',
			webView: true
		},
		{
			action: 'settings.accountManagement.consentsManage',
			isTrailingIconReversible: true,
			testID: 'settings-accountManagement-listItem',
			title: 'settings.accountManagement.consents',
			trailingIcon: 'ChevronRightIcon',
			webView: true
		}
	] as Array<PreParsedSectionItemsSettingProps>,
	title: 'settings.accountManagement.accountManagement'
};

export const useSettingsSections = () => {
	const { settings } = useSettings();
	const { isThemeSelectionAllowed } = useAppTheme();
	const { tutorialsCompleted } = useTutorialsCompleted();
	const [generalSectionItems, setGeneralSectionItems] = useState(generalItems);

	const generalSection: PreParsedSectionSettingProps = useMemo(() => {
		return {
			items: generalSectionItems,
			title: 'onboard.settings.general'
		};
	}, [generalSectionItems]);

	useEffect(() => {
		setGeneralSectionItems(prevItems =>
			prevItems.map(item => {
				if (item.action === 'onboard.settings.enableTutorial') {
					return { ...item, actionDisabled: Object.keys(tutorialsCompleted).length === 0 };
				}

				return item;
			})
		);
	}, [tutorialsCompleted]);

	const isDisplayMap = {
		fetchFavoritePartner: settings.isFavouriteTeamEnabled,
		setTheme: isThemeSelectionAllowed
	};

	const filteredGeneralSection = useFilterOtherSection(generalSection, isDisplayMap);

	const securitySection = useSecuritySection();

	return useMemo<Array<PreParsedSectionSettingProps>>(
		() => compact([securitySection, filteredGeneralSection, accountSection]),
		[filteredGeneralSection, securitySection]
	);
};
